import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Header } from "../components/Header";
import { Breadcrumbs, PublicFooter } from "../components/PublicChrome";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ReviewStrip, useReviews, withRatingLd } from "../components/ReviewStrip";
import { CONTENT, GUIDES } from "../lib/content";
import { useSeo, breadcrumbs, ORGANIZATION, SITE_URL } from "../lib/seo";
import { SIZES } from "../lib/shield";

const ldFor = (c, path, crumbs, rv) => {
  const graph = [ORGANIZATION, breadcrumbs(crumbs)];
  if (c.faq?.length) graph.push({ "@type": "FAQPage", mainEntity: c.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
  if (c.kind === "guide") graph.push({ "@type": "Article", headline: c.h1, description: c.description, image: `${SITE_URL}${c.image}`, datePublished: c.date, author: { "@id": `${SITE_URL}/#org` }, publisher: { "@id": `${SITE_URL}/#org` }, mainEntityOfPage: `${SITE_URL}${path}` });
  else graph.push(withRatingLd({ "@type": "Product", name: c.h1, description: c.description, image: `${SITE_URL}${c.image}`, brand: { "@type": "Brand", name: "sportsshields.ie" },
    offers: { "@type": "AggregateOffer", lowPrice: (SIZES[0].price / 100).toFixed(2), highPrice: (SIZES[3].price / 100).toFixed(2), priceCurrency: "EUR", offerCount: SIZES.length, url: `${SITE_URL}${path}` } }, rv));
  return { "@context": "https://schema.org", "@graph": graph };
};

const Section = ({ s, i }) => (
  <section className="space-y-3" data-testid={`content-section-${i}`}>
    <h2 className="font-display text-2xl font-black uppercase leading-tight">{s.h}</h2>
    {s.p?.map((t) => <p key={t} className="text-slate-700">{t}</p>)}
    {s.list && <ul className="space-y-2">{s.list.map((t) => <li key={t} className="flex gap-2 text-slate-700"><Check className="mt-1 h-4 w-4 shrink-0 text-lime-600" />{t}</li>)}</ul>}
  </section>
);

const Hero = ({ c, guide }) => (
  <section className={`grid items-center gap-8 ${guide ? "" : "lg:grid-cols-[1fr_1.05fr]"}`}>
    <div>
      {c.eyebrow && <div className="eyebrow">{c.eyebrow}</div>}
      <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl" data-testid="content-title">{c.h1}</h1>
      <p className="mt-4 max-w-2xl text-base text-slate-700 md:text-lg" data-testid="content-intro">{c.intro}</p>
      {!guide && <Link to="/design" className="btn-blaze mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-sm" data-testid="content-cta-design">Design your shield <ArrowRight className="h-4 w-4" /></Link>}
    </div>
    <img src={c.image} alt={c.imageAlt} width="1200" height="800" className={`w-full border-2 border-slate-900 object-cover shadow-[8px_8px_0_0_#0f172a] ${guide ? "max-h-96" : "aspect-[3/2]"}`} data-testid="content-hero-image" />
  </section>
);

export default function ContentPage() {
  const path = useLocation().pathname.replace(/\/$/, "");
  const c = CONTENT[path];
  const guide = c.kind === "guide";
  const crumbs = [["Home", "/"], ...(guide ? [["Guides", "/guides"]] : []), [c.crumb, path]];
  const rv = useReviews();
  useSeo({ title: c.title, description: c.description, path, canonical: c.canonical, image: `${SITE_URL}${c.image}`, jsonLd: ldFor(c, path, crumbs, rv) });
  return (
    <div className="min-h-screen">
      <Header onNew={() => { window.location.href = "/design"; }} />
      <main className="mx-auto max-w-6xl space-y-12 px-4 py-10 sm:px-6" data-testid={`content-page-${path.split("/").pop()}`}>
        <Breadcrumbs items={crumbs} />
        <Hero c={c} guide={guide} />
        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
          <article className="space-y-8">
            {c.sections.map((s, i) => <Section key={s.h} s={s} i={i} />)}
            {c.examples && <Section s={{ h: "Ideas for your shield", list: c.examples }} i="examples" />}
            {!guide && c.kind === "sport" && <Steps />}
            {!guide && <SizeGuide />}
            {!guide && <Section s={{ h: "Why families and clubs choose us", list: WHY }} i="why" />}
          </article>
          <aside className="space-y-6">
            <ReviewStrip compact />
            <nav className="border-2 border-slate-900 bg-white p-4" aria-label="Related pages" data-testid="content-related">
              <div className="font-bold uppercase">Related</div>
              <ul className="mt-2 space-y-1.5 text-sm">{c.related.map(([to, l]) => <li key={to}><Link to={to} className="font-semibold text-lime-700 underline hover:text-lime-800">{l}</Link></li>)}</ul>
            </nav>
            {guide && <Link to="/design" className="btn-blaze flex items-center justify-center gap-2 px-6 py-3.5 text-sm" data-testid="content-cta-design">Design a shield <ArrowRight className="h-4 w-4" /></Link>}
          </aside>
        </div>
        {c.faq?.length > 0 && (
          <section className="max-w-3xl">
            <h2 className="font-display text-2xl font-black uppercase">Questions</h2>
            <Accordion type="single" collapsible className="mt-2" data-testid="content-faq">
              {c.faq.map(([q, a], i) => (
                <AccordionItem key={q} value={q}>
                  <AccordionTrigger className="text-left font-bold" data-testid={`content-faq-q${i}`}>{q}</AccordionTrigger>
                  <AccordionContent className="text-slate-700">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

const WHY = [
  "Printed by Business Print in Dublin – a real Irish print shop, not an overseas marketplace",
  "Free automatic background removal on every photo",
  "Design on your phone in a few minutes, and save a draft to finish later",
  "Club colours, crests, county badges and positions for soccer, GAA and rugby",
  "Squad links so coaches can let every parent design and pay for their own",
  "Gift vouchers, click & collect and delivery anywhere in Ireland",
];
const FIT = { small: "Shelves, desks and whole-squad orders", medium: "Bedroom walls – our most popular gift size", large: "Statement piece above a bed or in a hallway", xl: "Clubhouses and the big end-of-season award" };

const SizeGuide = () => (
  <section className="space-y-3" data-testid="content-size-guide">
    <h2 className="font-display text-2xl font-black uppercase">Size guide</h2>
    <p className="text-slate-700">Every shield is printed on rigid board and cut to the shield shape, ready to hang or stand. Not sure which to pick? Medium suits most bedroom walls; Small keeps squad orders affordable.</p>
    <table className="w-full border-2 border-slate-900 bg-white text-left text-sm">
      <thead className="bg-slate-900 text-white"><tr><th className="p-2">Size</th><th className="p-2">Approx. size</th><th className="p-2">Price</th><th className="hidden p-2 sm:table-cell">Best for</th></tr></thead>
      <tbody>{SIZES.map((z) => (
        <tr key={z.id} className="border-t border-slate-200"><td className="p-2 font-bold">{z.name}</td><td className="p-2">{z.h / 10} cm tall</td><td className="p-2">€{(z.price / 100).toFixed(0)}</td><td className="hidden p-2 text-slate-600 sm:table-cell">{FIT[z.id]}</td></tr>
      ))}</tbody>
    </table>
    <Link to="/guides/choosing-shield-size" className="text-sm font-semibold text-lime-700 underline">Read the full size guide</Link>
  </section>
);

const Steps = () => (
  <section className="space-y-3">
    <h2 className="font-display text-2xl font-black uppercase">How it works</h2>
    <ol className="grid gap-3 sm:grid-cols-2">
      {["Upload a photo – we remove the background automatically", "Add the name, number, position, colours and crest", "Pick six stats and a background style", "Choose a size – we print, cut and deliver across Ireland"].map((t, i) => (
        <li key={t} className="flex gap-3 border-2 border-slate-900 bg-white p-3 text-sm"><span className="flex h-7 w-7 shrink-0 items-center justify-center bg-slate-900 font-black text-lime-300">{i + 1}</span>{t}</li>
      ))}
    </ol>
  </section>
);

export const GuidesIndex = () => {
  useSeo({ title: "Guides – Fundraising, Gifts & Shield Tips | sportsshields.ie", description: "Helpful guides for Irish clubs and families: fundraising ideas, gift ideas for young players, choosing a shield size and taking the perfect player photo.", path: "/guides" });
  return (
    <div className="min-h-screen">
      <Header onNew={() => { window.location.href = "/design"; }} />
      <main className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:px-6" data-testid="guides-page">
        <Breadcrumbs items={[["Home", "/"], ["Guides", "/guides"]]} />
        <h1 className="font-display text-4xl font-black uppercase leading-none sm:text-5xl lg:text-6xl">Guides</h1>
        <div className="grid gap-6 sm:grid-cols-2">
          {Object.entries(GUIDES).map(([to, g]) => (
            <Link key={to} to={to} className="card-lift group border-2 border-slate-900 bg-white shadow-[6px_6px_0_0_#0f172a]" data-testid={`guide-card-${to.split("/").pop()}`}>
              <img src={g.image} alt={g.imageAlt} loading="lazy" className="aspect-[3/2] w-full object-cover" />
              <div className="p-4"><h2 className="text-lg font-bold group-hover:underline">{g.h1}</h2><p className="mt-1 text-sm text-slate-600">{g.description}</p></div>
            </Link>
          ))}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};
